/* eslint no-console:0 */
import _ from 'lodash';
import io from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { EMITTER_CONSTANTS, FRONT_END_ERRORS_EMITTER_CONSTANTS, SOCKET } from '@/shared/config';
import { logError } from '@/shared/lib/apiHelpers';
import auth from './auth/auth';
import getAccessToken from './auth/authToken';
import emitter from './eventEmitter';
import { reportSocketAck } from './devLog';

// Socket event payloads come from the server with no shared contract file to
// derive types from; `msg`/`data`/`error` params below are `any` until a
// typed API layer lands for socket events.
class SocketClient {
  socket?: Socket | null;

  isConnected: boolean;

  isLazyLoading: boolean;

  rooms: { name: string; id: string }[];

  intervalRoom: ReturnType<typeof setInterval> | null;

  // Dead: never read anywhere outside this declaration/init (grepped repo-wide). Left as-is —
  // out of Part 2's scope (not one of the 24 relocated listeners or the 4 relocated queues).
  intervalJoinRoomEvent: ReturnType<typeof setInterval> | null;

  queueRoomEvent: any[];

  timeOutRoomReport: ReturnType<typeof setTimeout> | undefined;

  intervalJoinRoomReport: ReturnType<typeof setInterval> | null;

  queueRoomReport: any[];

  constructor() {
    this.isConnected = false;
    this.isLazyLoading = false;
    this.rooms = [];
    this.intervalRoom = null;

    this.intervalJoinRoomEvent = null;
    this.queueRoomEvent = [];

    this.timeOutRoomReport = undefined;
    this.intervalJoinRoomReport = null;
    this.queueRoomReport = [];
  }

  getAuth = async () => {
    const accessToken = await getAccessToken();
    auth.setOriginalToken(accessToken);
    return {
      accessToken,
    };
  };

  connectToServer = async () => {
    try {
      if (!this.socket) {
        this.socket = io(SOCKET, {
          forceNew: true,
          transports: ['websocket'],
          auth: async (cb) => cb(await this.getAuth()),
          // Prevent disconnect socket before unload to send close event to server
          closeOnBeforeunload: false,
        });
        this.socket!.on('connect', this.connectListener);
      }
    } catch (error) {
      logError('Failed to connect socket: ', error);
      this.disconnectSocket();
    }
  };

  connectListener = () => {
    this.isConnected = true;

    auth.setSocketId(this.socket?.id);

    this.emitRoom('maintenance-room');

    this.socket!.off('reconnect');
    this.socket!.on('disconnect', this.disconnectListener);

    // The 24 registrations that used to be here (roomUsers .. recaptureAutoEvents) moved to
    // app/socket/listeners.ts + registry.ts (Part 2, Task 15) — attached by app/socket/registry.ts
    // in response to this emit, at the exact point in this method they used to be registered
    // synchronously. SOCKET_CONNECTED below fires 1s later and must stay separate: it's a stable
    // public signal other consumers (e.g. pages/report/useAiReportPage.tsx) key off of, and delaying
    // listener attach to match it would leave every server event dropped for that whole second.
    emitter.emit(EMITTER_CONSTANTS.SOCKET_ATTACH_LISTENERS);

    // this.registerSocketListener('commandPending', this.commandPendingListener);
    // this.registerSocketListener('aiProcessSaved', this.aiProcessSavedListener);
    // this.registerSocketListener('reCapturedEvents', this.reCapturedEventsListener);
    // this.registerSocketListener('commandExecuted', this.commandExecutedListener);
    setTimeout(() => {
      emitter.emit(EMITTER_CONSTANTS.SOCKET_CONNECTED);
      console.log('socket io - on connect', Date.now());
    }, 1000); // Wait for socket to be connected
  };

  disconnectListener = (error: string) => {
    console.log('socket io - on disconnect:', error);

    // emitter.emit(EMITTER_CONSTANTS.LOST_CONNECTION, CONNECTION_TYPE.SOCKET);
    // alert('Sorry for the inconvenience!\nThe system ran into a problem and needs to reload a page.');
    // window.location.reload();

    this.isConnected = false;
    this.socket!.io.on('reconnect', this.reconnectListener);

    this.socket!.off('connect');
    this.socket!.off('disconnect');

    // this.socket.off('studyEvent');
    // this.socket.off('deviceEvent');
    // this.socket.off('lastSync');
    // this.socket.off('studyUpdated');
    // this.socket.off('studyDeleted');

    // this.socket.off('isPreparingFullData');
    // this.socket.off('newEvent');
    // this.socket.off('multipleEventsClosed');
    // this.socket.off('facilityUpdated');
    // this.socket.off('bucketChanged');
    // this.socket.off('moreStudies');

    // aiProcessSaved was never registered (see the commented-out registration below) — this .off
    // is pre-existing dead residue, left as-is.
    this.socket!.off('aiProcessSaved');

    if (error && error.trim() === 'io server disconnect') {
      window.isSessionExpired = true;
      emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN, 'request');
      this.socket = null;
    }

    emitter.emit(EMITTER_CONSTANTS.SOCKET_DISCONNECTED);
  };

  reconnectListener = () => {
    console.log('on reconnect');
    this.socket!.on('connect', this.connectListener);
    // window.location.reload();
    // Remove duplicate data
    const uniqRooms = _.uniqWith(this.rooms, _.isEqual);
    console.log('uniqRooms', uniqRooms);
    _.forEach(uniqRooms, (room) => {
      const { name, id } = room;
      switch (name) {
        case 'joinReportRoom': {
          this.sendJoinReportRoom(id);
          break;
        }
        default: {
          this.emitIntervalEvent(name, id);
          break;
        }
      }
    });
    emitter.emit(EMITTER_CONSTANTS.SOCKET_RECONNECTED);
  };

  emitSocketSpamTracking = (eventName: string) => {
    emitter.emit(FRONT_END_ERRORS_EMITTER_CONSTANTS.SOCKET_SPAM_TRACKING, eventName);
  };

  registerSocketListener = (eventName: string, handler: (...args: any[]) => any) => {
    if (!this.socket) {
      return;
    }
    this.socket!.on(eventName, (...args: any[]) => {
      this.emitSocketSpamTracking(eventName);
      return handler(...args);
    });
  };

  commandExecutedListener = (msg: any) => {
    console.log('socket io - commandExecutedListener', msg);
    emitter.emit(EMITTER_CONSTANTS.COMMAND_EXECUTED, msg);
  };

  commandPendingListener = (msg: any) => {
    console.log('socket io - commandPendingListener', msg);
    emitter.emit(EMITTER_CONSTANTS.COMMAND_PENDING, msg);
  };

  aiProcessSavedListener = (msg: any) => {
    console.log('socket io - aiProcessSavedListener', msg);
    emitter.emit(EMITTER_CONSTANTS.AI_PROCESS_SAVED, msg);
  };

  reCapturedEventsListener = async (msg: any) => {
    console.log('socket io - reCapturedEventsListener', msg);
    emitter.emit(EMITTER_CONSTANTS.RE_CAPTURE_EVENTS, msg);
  };

  emitWithAck = (status: string, id: string) => {
    this.socket!.timeout(30000).emit(status, id, (err: any, response: any) => {
      if (err) {
        // Server did not acknowledge within the timeout.
        reportSocketAck(status, 'timeout', id);
        return;
      }
      if (!response?.success) {
        // Server acknowledged but rejected the request.
        reportSocketAck(status, 'error', id, response?.error);
        return;
      }
      reportSocketAck(status, 'ok', id, response);
    });
  };

  emitIntervalEvent = (status: string, id: string) => {
    console.log('EMITEVENT', status, id, this.isConnected);
    if (this.isConnected) {
      this.emitWithAck(status, id);
    } else {
      const interval = setInterval(() => {
        if (this.isConnected) {
          this.emitWithAck(status, id);
          clearInterval(interval);
        }
        if (!auth.token()) {
          clearInterval(interval);
        }
      }, 1000);
    }
  };

  handleStoreRooms = (name: string, id: string) => {
    if (!_.some(this.rooms, (x) => x.id === id)) {
      this.rooms.push({ name, id });
    }
  };

  emitRoom = (id: string) => {
    if (id && id !== 'undefined') {
      this.handleStoreRooms('room', id);
      this.emitIntervalEvent('room', id);
    }
  };

  // send join report room
  /**
   * Joins a report room so the server streams that report's realtime events. Queues the message, then emits joinReportRoom and records the room immediately when the socket is already connected, otherwise starts a one second poll that flushes the deduplicated queue once the connection comes up and clears itself.
   *
   * @param room - Identifier of the report room to join.
   * @param isPreview - Marks the join as coming from a preview view.
   * @returns Nothing.
   */
  sendJoinReportRoom(room: string, isPreview = false) {
    const msg = {
      room,
      isPreview,
    };
    this.queueRoomReport.push(msg);
    if (this.isConnected) {
      console.log('[ ==> ] sendJoinReportRoom connected', msg);
      this.handleStoreRooms('joinReportRoom', msg.room);
      this.socket!.emit('joinReportRoom', msg);
    } else if (!this.intervalJoinRoomReport) {
      this.intervalJoinRoomReport = setInterval(() => {
        if (this.isConnected) {
          const datas = _.assign([], this.queueRoomReport);
          this.queueRoomReport.splice(0, datas.length);
          // Remove duplicate data
          const uniqDatas = _.uniqWith(datas, _.isEqual);
          _.forEach(uniqDatas, (e) => {
            console.log('[ ==> ] sendJoinReportRoom', e);
            this.handleStoreRooms('joinReportRoom', e.room);
            this.socket!.emit('joinReportRoom', e);
          });
          clearInterval(this.intervalJoinRoomReport);
          this.intervalJoinRoomReport = null;
        }
      }, 1000);
    }
  }

  /**
   * Tears down the socket.io connection and forgets the joined rooms. Disconnects the underlying socket when one exists, nulls the handle, and empties the rooms list.
   *
   * @returns Nothing.
   */
  disconnectSocket() {
    console.log('socket io disconnect', this.socket);
    if (this.socket) {
      this.socket!.disconnect();
    }
    this.socket = null;
    this.rooms.length = 0;
  }

  getSocket = (): Socket | null => this.socket ?? null;
}

const staticSocket = new SocketClient();

export default staticSocket;
